// * IMPORTANT: This file was generated by shadcn. Do not change
// * it manually unless you absolutely know what you are doing.

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Removed `focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2`.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300",
  {
    variants: {
      variant: {
        // Swapped `slate` for `primary` for the default variant. Also changed text from `text-slate-50` to `text-white`.
        default: "bg-primary-900 text-white hover:bg-primary-900/90 dark:bg-primary-50 dark:text-primary-900 dark:hover:bg-primary-50/90",
        destructive:
          // Swapped `bg-red-500` for `bg-red-600` for non-dark mode styles.
          "bg-red-600 text-slate-50 hover:bg-red-600/90 dark:bg-red-900 dark:text-slate-50 dark:hover:bg-red-900/90",
        // Swapped `slate` for `primary` for the outline variant for non-dark mode styles. Added `text-primary-900` for non-dark mode styles.
        outline:
          "border border-primary-900 bg-white text-primary-900 hover:bg-primary-100 hover:text-primary-900 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800 dark:hover:text-slate-50",
        // Copy of the original `outline` variant with `text-slate-700` for non-dark mode text styles.
        outlineSecondary:
          "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800 dark:hover:text-slate-50",
        // Copy of the original `outline` variant with `text-red-600` for text styles and `hover:bg-red-50` for hover styles. Would need to add dark mode styles.
        outlineDestructive:
          "border border-red-600 bg-white text-red-600 hover:bg-red-50 hover:text-red-600",
        // Copy of the original `outline` variant with `text-green-600` for text styles and `hover:bg-green-50` for hover styles. Would need to add dark mode styles.
        outlineGreen:
          "border border-green-600 bg-white text-green-600 hover:bg-green-50 hover:text-green-600",
        secondary:
          "bg-slate-100 text-slate-900 hover:bg-slate-100/80 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-800/80",
        // Added `text-slate-700`.
        // Update `hover:text-slate-900` to `hover:text-slate-700`.
        ghost: "text-slate-700 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-50",
        link: "text-slate-900 underline-offset-4 hover:underline dark:text-slate-50",
        // A bespoke variant for filter and sort buttons in the active state.
        filterOrSortActive: "border border-primary-700 bg-primary-300 text-slate-700 hover:bg-primary-400 hover:text-slate-700",
        // A bespoke yellow variant that is a combination of outline and secondary.
        yellow: "border border-yellow-500 bg-yellow-50 text-slate-700 hover:bg-yellow-50/80",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
